import { LandingPageData } from 'Interfaces/ProductWikiInterfaces';
import type { ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces/landingPage.types';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { productWikiServices } from 'services/productWikiServices';

/**
 * Product Wiki Store
 * Manages state for Product Wiki module including landing page data
 */
export class LandingPageStore {
  // Landing Page State
  landingPageData: LandingPageData | null = null;
  isLandingPageLoading: boolean = false;
  landingPageError: string | null = null;

  // Contract Expirations Loading State
  isContractExpirationsLoading: boolean = false;

  // ANDA Updates Loading State
  isAndaUpdatesLoading: boolean = false;

  // Loss of Exclusivity Loading State
  isLossOfExclusivityLoading: boolean = false;

  // Pre-Solicitation Alerts Loading State
  isPreSolicitationAlertsLoading: boolean = false;

  // Dropdown selected values - shared across all components
  selectedContractExpirationsPeriod: number = 90;
  selectedAndaUpdatesPeriod: number = 90;
  selectedLossOfExclusivityPeriod: number = 90;
  selectedPreSolicitationAlertsFilter: string = 'duedates';

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetch landing page data from API
   */
  fetchLandingPageData = async () => {
    this.isLandingPageLoading = true;
    this.landingPageError = null;

    try {
      const response = await productWikiServices.getLandingPageData();

      runInAction(() => {
        if (response.data) {
          this.landingPageData = {
            stats: response.data.stats || [],
            contractExpirations: response.data.contract_expirations || [],
            andaUpdates: response.data.anda_updates || [],
            preSolicitationAlerts: response.data.pre_solicitation_alerts || [],
            lossOfExclusivity: response.data.loss_of_exclusivity || [],
            marketIntelligence: response.data.market_intelligence || [],
            searchSuggestions: response.data.search_suggestions || [],
          };
        }
        this.isLandingPageLoading = false;
      });
    } catch (_error: any) {
      runInAction(() => {
        this.isLandingPageLoading = false;
        this.landingPageError =
          _error?.response?.data?.message || 'Failed to fetch landing page data';
        toast.error('Failed to load landing page data. Please try again.');
      });
    }
  };

  /**
   * Fetch contract expirations by days
   * @param days - Number of days to look forward
   */
  fetchContractExpirations = async (days: number) => {
    this.isContractExpirationsLoading = true;
    this.selectedContractExpirationsPeriod = days;

    try {
      const response = await productWikiServices.getContractExpirations(days);

      runInAction(() => {
        if (response.data && this.landingPageData) {
          // Update only contract expirations, keep other data intact
          this.landingPageData = {
            ...this.landingPageData,
            contractExpirations: response.data,
          };
        }
        this.isContractExpirationsLoading = false;
      });
    } catch (_error: any) {
      runInAction(() => {
        this.isContractExpirationsLoading = false;
        toast.error('Failed to load contract expirations. Please try again.');
      });
    }
  };

  /**
   * Fetch ANDA updates by days
   * @param days - Number of days to look forward
   */
  fetchAndaUpdates = async (days: number) => {
    this.isAndaUpdatesLoading = true;
    this.selectedAndaUpdatesPeriod = days;

    try {
      const response = await productWikiServices.getAndaUpdates(days);

      runInAction(() => {
        if (response.data && this.landingPageData) {
          // Update only ANDA updates, keep other data intact (API returns ANDA-shaped cards)
          this.landingPageData = {
            ...this.landingPageData,
            andaUpdates: (response.data ?? []) as ANDAInfoCardData[],
          };
        }
        this.isAndaUpdatesLoading = false;
      });
    } catch (_error: any) {
      runInAction(() => {
        this.isAndaUpdatesLoading = false;
        toast.error('Failed to load ANDA updates. Please try again.');
      });
    }
  };

  /**
   * Fetch Loss of Exclusivity by days
   * @param days - Number of days to look forward
   */
  fetchLossOfExclusivity = async (days: number) => {
    this.isLossOfExclusivityLoading = true;
    this.selectedLossOfExclusivityPeriod = days;

    try {
      const response = await productWikiServices.getLossOfExclusivity(days);

      runInAction(() => {
        if (response.data && this.landingPageData) {
          // Update only Loss of Exclusivity, keep other data intact
          this.landingPageData = {
            ...this.landingPageData,
            lossOfExclusivity: response.data,
          };
        }
        this.isLossOfExclusivityLoading = false;
      });
    } catch (_error: any) {
      runInAction(() => {
        this.isLossOfExclusivityLoading = false;
        toast.error('Failed to load Loss of Exclusivity. Please try again.');
      });
    }
  };

  /**
   * Fetch Pre-Solicitation Alerts by type
   * @param type - Type of filter - 'duedate' or 'deadline'
   */
  fetchPreSolicitationAlerts = async (type: string) => {
    this.isPreSolicitationAlertsLoading = true;
    // Map API type to frontend filter value
    this.selectedPreSolicitationAlertsFilter = type === 'duedate' ? 'duedates' : 'deadlines';

    try {
      const response = await productWikiServices.getPreSolicitationAlerts(type);

      runInAction(() => {
        if (response.data && this.landingPageData) {
          // Update only Pre-Solicitation Alerts, keep other data intact
          this.landingPageData = {
            ...this.landingPageData,
            preSolicitationAlerts: response.data,
          };
        }
        this.isPreSolicitationAlertsLoading = false;
      });
    } catch (_error: any) {
      runInAction(() => {
        this.isPreSolicitationAlertsLoading = false;
        toast.error('Failed to load Pre-Solicitation Alerts. Please try again.');
      });
    }
  };

  /**
   * Reset dropdown values to defaults
   */
  resetDropdownValues = () => {
    this.selectedContractExpirationsPeriod = 90;
    this.selectedAndaUpdatesPeriod = 90;
    this.selectedLossOfExclusivityPeriod = 90;
    this.selectedPreSolicitationAlertsFilter = 'duedates';
  };

  /**
   * Reset landing page data
   */
  resetLandingPageData = () => {
    this.landingPageData = null;
    this.isLandingPageLoading = false;
    this.landingPageError = null;
    this.isContractExpirationsLoading = false;
    this.isAndaUpdatesLoading = false;
    this.isLossOfExclusivityLoading = false;
    this.isPreSolicitationAlertsLoading = false;
    this.resetDropdownValues();
  };

  /**
   * Reset entire store
   */
  resetStore = () => {
    this.resetLandingPageData();
  };
}
